/**
 * FillableFormEditor - HR side
 *
 * Modal that renders a PDF page-by-page using react-pdf and lets HR:
 *  - Auto-detect existing AcroForm fields (POST /detect-fillable)
 *  - Draw new fields by clicking + dragging a rect on the page
 *  - Rename each field, choose type (text/signature/date/checkbox),
 *    mark required, choose assignee (employee/hr)
 *  - Save the config back to the file record
 *
 * Coordinates are stored in PDF POINTS (1/72 inch), origin bottom-left,
 * matching what the backend needs to overlay values with reportlab.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Loader2,
  Sparkles,
  Plus,
  Save,
  Trash2,
  X,
  MousePointer2,
  MoveDiagonal,
  IdCard,
  CheckSquare,
  Wand2,
  Eye,
} from "lucide-react";
import AutoFillPanel, { fieldAutoFill } from "./AutoFillPanel";
import NursePreviewDialog from "./NursePreviewDialog";

// Load worker from unpkg (matches installed pdfjs-dist version exactly)
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`; // served locally — no CDN dependency

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREFILL_TOKENS = [
  { token: "{{agency_name}}", label: "Agency name" },
  { token: "{{agency_address}}", label: "Agency address" },
  { token: "{{agency_phone}}", label: "Agency phone" },
  { token: "{{agency_fax}}", label: "Agency fax" },
  { token: "{{agency_ein}}", label: "Agency EIN / Tax ID" },
  { token: "{{agency_npi}}", label: "Agency NPI" },
  { token: "{{agency_medicaid_id}}", label: "Medicaid provider #" },
  { token: "{{employee_name}}", label: "Employee name" },
  { token: "{{employee_start_date}}", label: "Employee start date" },
  { token: "{{employee_email}}", label: "Employee email" },
  { token: "{{employee_phone}}", label: "Employee phone" },
  { token: "{{today}}", label: "Today's date" },
  { token: "{{i9_list_a_title}}", label: "I-9 List A — title" },
  { token: "{{i9_list_a_authority}}", label: "I-9 List A — authority" },
  { token: "{{i9_list_a_number}}", label: "I-9 List A — number" },
  { token: "{{i9_list_a_expiration}}", label: "I-9 List A — expiration" },
  { token: "{{i9_list_b_title}}", label: "I-9 List B — title" },
  { token: "{{i9_list_b_authority}}", label: "I-9 List B — authority" },
  { token: "{{i9_list_b_number}}", label: "I-9 List B — number" },
  { token: "{{i9_list_b_expiration}}", label: "I-9 List B — expiration" },
  { token: "{{i9_list_c_title}}", label: "I-9 List C — title" },
  { token: "{{i9_list_c_authority}}", label: "I-9 List C — authority" },
  { token: "{{i9_list_c_number}}", label: "I-9 List C — number" },
  { token: "{{i9_list_c_expiration}}", label: "I-9 List C — expiration" },
  { token: "{{employee_legal_last_name}}", label: "I-9 Sec. 1 — legal last name" },
  { token: "{{employee_legal_first_name}}", label: "I-9 Sec. 1 — legal first name" },
  { token: "{{employee_middle_initial}}", label: "I-9 Sec. 1 — middle initial" },
  { token: "{{employee_other_last_names}}", label: "I-9 Sec. 1 — other last names" },
  { token: "{{employee_address_street}}", label: "I-9 Sec. 1 — street address" },
  { token: "{{employee_address_apt}}", label: "I-9 Sec. 1 — apt. number" },
  { token: "{{employee_address_city}}", label: "I-9 Sec. 1 — city" },
  { token: "{{employee_address_state}}", label: "I-9 Sec. 1 — state" },
  { token: "{{employee_address_zip}}", label: "I-9 Sec. 1 — ZIP code" },
  { token: "{{employee_dob}}", label: "I-9 Sec. 1 — date of birth" },
  { token: "{{employee_ssn}}", label: "I-9 Sec. 1 — Social Security number" },
  { token: "{{client_name}}", label: "Client — full name" },
  { token: "{{client_first_name}}", label: "Client — first name" },
  { token: "{{client_last_name}}", label: "Client — last name" },
  { token: "{{client_dob}}", label: "Client — date of birth" },
  { token: "{{client_address}}", label: "Client — address" },
  { token: "{{client_phone}}", label: "Client — phone" },
  { token: "{{client_medicaid_id}}", label: "Client — Medicaid ID" },
  { token: "{{client_medicare_id}}", label: "Client — Medicare ID" },
  { token: "{{client_diagnosis}}", label: "Client — primary diagnosis" },
  { token: "{{client_allergies}}", label: "Client — allergies" },
  { token: "{{client_physician}}", label: "Client — physician" },
  { token: "{{client_physician_phone}}", label: "Client — physician phone" },
  { token: "{{client_emergency_contact}}", label: "Client — emergency contact" },
  { token: "{{rn_name}}", label: "RN Assessment — nurse name" },
  { token: "{{visit_date}}", label: "RN Assessment — visit date" },
  { token: "{{visit_time}}", label: "RN Assessment — visit time" },
  { token: "{{client_pcw_name}}", label: "Client — caregiver (PCW) name" },
];

// Map an I-9 Section 1 (employee) field to its editable default-value token.
export function i9Section1Token(field) {
  const s = `${field.label || ""} ${field.pdf_field_name || ""}`.toLowerCase();
  if (/preparer|translator|employer|authorized representative|section\s*2|section\s*3|list\s*[abc]/.test(s)) return null;
  if (/other last names?/.test(s)) return "{{employee_other_last_names}}";
  if (/last name|family name/.test(s)) return "{{employee_legal_last_name}}";
  if (/first name|given name/.test(s)) return "{{employee_legal_first_name}}";
  if (/middle initial/.test(s)) return "{{employee_middle_initial}}";
  if (/apt|apartment/.test(s)) return "{{employee_address_apt}}";
  if (/street|address \(street|^address/.test(s)) return "{{employee_address_street}}";
  if (/city|town/.test(s)) return "{{employee_address_city}}";
  if (/zip/.test(s)) return "{{employee_address_zip}}";
  if (/\bstate\b/.test(s) && !/issuing|state file/.test(s)) return "{{employee_address_state}}";
  if (/date of birth|\bdob\b|birth/.test(s)) return "{{employee_dob}}";
  if (/social security|\bssn\b/.test(s)) return "{{employee_ssn}}";
  if (/e-?mail/.test(s)) return "{{employee_email}}";
  if (/telephone|phone/.test(s)) return "{{employee_phone}}";
  return null;
}

// Map an I-9 Section 2 field (by label / PDF field name) to its prefill token.
export function i9Section2Token(field) {
  const s = `${field.label || ""} ${field.pdf_field_name || ""}`.toLowerCase();
  const kind = /document\s*title|doc\.?\s*title/.test(s) ? "title"
    : /issuing\s*authority/.test(s) ? "authority"
    : /document\s*(number|no\b|#)|doc\.?\s*(number|no\b|#)/.test(s) ? "number"
    : /expir/.test(s) ? "expiration" : null;
  if (!kind) return null;
  if (/section\s*1|section\s*3|supplement|reverif|rehire|alien|uscis|i-94|passport\s*number|form\s*i-94/.test(s)) return null;
  const list = /list\s*b/.test(s) ? "b" : /list\s*c/.test(s) ? "c" : "a";
  if (list === "a" && /\b(2|3)\b|second|third/.test(s.replace(/list\s*a|section\s*2/g, ""))) return null;
  return `{{i9_list_${list}_${kind}}}`;
}

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "signature", label: "Signature" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
  { value: "ink", label: "Drawing" },
];

const TYPE_COLOR = {
  text: "border-blue-400 bg-blue-100/60",
  signature: "border-purple-500 bg-purple-100/60",
  date: "border-emerald-400 bg-emerald-100/60",
  checkbox: "border-amber-400 bg-amber-100/60",
  ink: "border-indigo-400 bg-indigo-100/50",
};

// Render every page at 1.4x so it's readable but not massive
const SCALE = 1.4;

function newFieldId() {
  return `fld_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function FillableFormEditor({
  open,
  onClose,
  file,
  employeeId,
  onSaved,
  // Optional overrides so the editor can work on non-vault PDFs (e.g. orientation kit docs)
  endpoints, // { pdf, info, detect, detectBody }
  onSaveFields, // async (fields, {page_count, page_sizes}) => void
  onClearFields, // async () => void
}) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageSizes, setPageSizes] = useState([]); // [[wPts, hPts], ...]
  const [pageRenderInfo, setPageRenderInfo] = useState({}); // {i: {width, height}}
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("select"); // "select" | "draw"
  const [drawType, setDrawType] = useState("text");
  const [drawing, setDrawing] = useState(null); // {page, startX, startY, x, y, w, h}
  const [manip, setManip] = useState(null); // select mode: {kind: move|resize, id, page, startX, startY, orig, container}
  useEffect(() => {
    if (!manip) return;
    const up = () => setManip(null);
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [manip]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [guesses, setGuesses] = useState({}); // {field_id: token} label-based auto-fill guesses
  const [showLabels, setShowLabels] = useState(false); // false = tags only on hover / selection
  const [previewOpen, setPreviewOpen] = useState(false);
  const canvasRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);
  useEffect(() => {
    if (!open || loading) return;
    const el = canvasRef.current;
    if (!el) return;
    const update = () => setCanvasWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, loading]);
  const firstPageWidth = (pageSizes[0] || [612])[0];
  const renderScale = canvasWidth > 0 ? Math.min(SCALE, Math.max(0.5, (canvasWidth - 32) / firstPageWidth)) : SCALE; // fit landscape pages

  const guessKey = fields.map((f) => `${f.id}|${f.label}|${f.type}`).join("\n");
  useEffect(() => {
    if (!open || fields.length === 0) { setGuesses({}); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/employee-files/guess-tokens`, {
          fields: fields.map((f) => ({ id: f.id, label: f.label, type: f.type })),
        });
        if (!cancelled) setGuesses(res.data.guesses || {});
      } catch { /* hints only */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, guessKey]);

  const autoMapFields = () => {
    let n = 0;
    const next = fields.map((f) => {
      const g = guesses[f.id];
      if (!g || f.assignee === "hr" || f.no_autofill || (f.default_value || "").trim()) return f;
      n += 1;
      return { ...f, default_value: g };
    });
    setFields(next);
    if (n) toast.success(`${n} field${n === 1 ? "" : "s"} set to auto-fill from their labels — review the green badges, then Save.`);
    else toast.info("No additional fields recognized — pick an auto-fill value on each field yourself.");
  };

  useEffect(() => {
    if (!open || !file) return;
    let currentUrl = null;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch PDF blob and pdf info in parallel
        const pdfEndpoint = endpoints?.pdf || `${API}/employees/${employeeId}/files/${file.id}?inline=true`;
        const infoEndpoint = endpoints?.info || `${API}/employees/${employeeId}/files/${file.id}/pdf-info`;
        const [pdfRes, infoRes] = await Promise.all([
          axios.get(pdfEndpoint, { responseType: "blob" }),
          axios.get(infoEndpoint),
        ]);
        if (cancelled) return;
        currentUrl = URL.createObjectURL(pdfRes.data);
        setPdfUrl(currentUrl);
        setPageCount(infoRes.data.page_count);
        setPageSizes(infoRes.data.page_sizes || []);
        // Load existing fields if already fillable
        if (file.fillable_form?.fields?.length) {
          setFields(file.fillable_form.fields);
        } else {
          setFields([]);
        }
        setSelectedId(null);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not load PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setPdfUrl(null);
      setFields([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file, employeeId]);

  const detectFields = async (mode = "auto") => {
    setDetecting(true);
    try {
      const base = endpoints?.detect || `${API}/employees/${employeeId}/files/${file.id}/detect-fillable`;
      const res = await axios.post(
        `${base}${base.includes("?") ? "&" : "?"}mode=${mode}`,
        endpoints?.detectBody || undefined,
        { retryUnsafe: true }
      );
      const detected = res.data.fields || [];
      if (detected.length === 0) {
        toast.info(
          mode === "visual"
            ? "No boxes or blank lines found — scanned image PDFs can't be read; draw the fields with 'Add Field'"
            : "No form fields detected — try 'Detect boxes & blanks' or draw them with 'Add Field'"
        );
      } else {
        setFields(detected);
        const boxes = detected.filter((f) => f.type === "checkbox").length;
        toast.success(
          res.data.visual
            ? `Found ${boxes} checkbox${boxes === 1 ? "" : "es"} and ${detected.length - boxes} blank${detected.length - boxes === 1 ? "" : "s"} on the form — review and adjust, then Save`
            : `Auto-detected ${detected.length} field${detected.length > 1 ? "s" : ""}`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Detection failed");
    } finally {
      setDetecting(false);
    }
  };

  const saveConfig = async () => {
    if (fields.length === 0) {
      if (!window.confirm("No fields configured. Turn OFF fillable form for this file?")) return;
      try {
        if (onClearFields) {
          await onClearFields();
        } else {
          await axios.delete(`${API}/employees/${employeeId}/files/${file.id}/fillable-form`);
        }
        toast.success("Fillable form disabled");
        onSaved?.();
        onClose();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Save failed");
      }
      return;
    }
    setSaving(true);
    try {
      if (onSaveFields) {
        await onSaveFields(fields, { page_count: pageCount, page_sizes: pageSizes });
      } else {
        await axios.post(
          `${API}/employees/${employeeId}/files/${file.id}/fillable-form`,
          { fields }
        );
      }
      toast.success("Fillable form saved");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (id, patch) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const applyI9Preset = () => {
    let sec2 = 0;
    let sec1 = 0;
    const next = fields.map((f) => {
      const m2 = i9Section2Token(f);
      if (m2) { sec2 += 1; return { ...f, assignee: "hr", hr_value: m2 }; }
      const m1 = i9Section1Token(f);
      if (m1) { sec1 += 1; return { ...f, assignee: "employee", default_value: m1 }; }
      return f;
    });
    setFields(next);
    if (sec1 || sec2) toast.success(`I-9 preset applied — ${sec1} Section 1 field${sec1 === 1 ? "" : "s"} pre-filled from the new hire's background-check form, ${sec2} Section 2 field${sec2 === 1 ? "" : "s"} from approved ID documents. Remember to Save.`);
    else toast.info("No I-9 fields recognized (look for Last Name / Address / Date of Birth / Document Title / Issuing Authority labels)");
  };

  const removeField = (id) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // Convert click coords (px, top-left origin) to PDF points (bottom-left origin)
  const pxToPdf = (pageIndex, clientX, clientY, container) => {
    const rect = container.getBoundingClientRect();
    const [wPts, hPts] = pageSizes[pageIndex] || [612, 792];
    const scale = rect.width / wPts;
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    return {
      xPts: px / scale,
      yPts: hPts - py / scale,
    };
  };

  const onPageMouseDown = (pageIndex, e) => {
    if (mode !== "draw") return;
    const container = e.currentTarget;
    const start = pxToPdf(pageIndex, e.clientX, e.clientY, container);
    setDrawing({
      page: pageIndex,
      startX: start.xPts,
      startY: start.yPts,
      x: start.xPts,
      y: start.yPts,
      w: 0,
      h: 0,
      container,
    });
  };

  const onPageMouseMove = (pageIndex, e) => {
    if (manip && manip.page === pageIndex) {
      const cur = pxToPdf(pageIndex, e.clientX, e.clientY, manip.container);
      const dx = cur.xPts - manip.startX;
      const dy = cur.yPts - manip.startY;
      const [wPts, hPts] = pageSizes[pageIndex] || [612, 792];
      const o = manip.orig;
      if (manip.kind === "move") {
        updateField(manip.id, {
          x: Math.max(0, Math.min(wPts - o.width, o.x + dx)),
          y: Math.max(0, Math.min(hPts - o.height, o.y + dy)),
        });
      } else {
        // bottom-right handle: width grows with +dx, height grows downward (−dy in PDF space)
        const width = Math.max(4, o.width + dx);
        const height = Math.max(4, o.height - dy);
        updateField(manip.id, { width, height, y: o.y + o.height - height });
      }
      return;
    }
    if (mode !== "draw" || !drawing || drawing.page !== pageIndex) return;
    const cur = pxToPdf(pageIndex, e.clientX, e.clientY, drawing.container);
    const x = Math.min(drawing.startX, cur.xPts);
    const y = Math.min(drawing.startY, cur.yPts);
    const w = Math.abs(cur.xPts - drawing.startX);
    const h = Math.abs(cur.yPts - drawing.startY);
    setDrawing({ ...drawing, x, y, w, h });
  };

  const DEFAULT_SIZES = { text: [170, 16], date: [90, 16], checkbox: [12, 12], signature: [170, 36], ink: [240, 90] };

  const onPageMouseUp = () => {
    if (manip) { setManip(null); return; }
    if (mode !== "draw" || !drawing) return;
    const dragged = drawing.w > 5 && drawing.h > 5;
    const [dw, dh] = DEFAULT_SIZES[drawType] || DEFAULT_SIZES.text;
    const [wPts, hPts] = pageSizes[drawing.page] || [612, 792];
    // A plain click places a default-sized field at that spot (checkbox centred; text/date/signature sit on the click point)
    const x = dragged ? drawing.x : Math.max(0, Math.min(wPts - dw, drawType === "checkbox" ? drawing.startX - dw / 2 : drawing.startX));
    const y = dragged ? drawing.y : Math.max(0, Math.min(hPts - dh, drawing.startY - dh / 2));
    const id = newFieldId();
    const newField = {
      id,
      page: drawing.page,
      x,
      y,
      width: dragged ? drawing.w : dw,
      height: dragged ? drawing.h : dh,
      type: drawType,
      label: `${drawType} field ${fields.length + 1}`,
      required: false,
      assignee: "employee",
    };
    setFields((prev) => [...prev, newField]);
    setSelectedId(id);
    setDrawing(null);
  };

  const selected = fields.find((f) => f.id === selectedId);

  // Group fields by page for rendering overlays
  const fieldsByPage = useMemo(() => {
    const map = {};
    fields.forEach((f) => {
      (map[f.page] ||= []).push(f);
    });
    return map;
  }, [fields]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-6xl w-[96vw] h-[92vh] p-0 flex flex-col"
        data-testid="fillable-form-editor"
        onEscapeKeyDown={(e) => {
          if (mode === "draw") {
            e.preventDefault();
            setMode("select");
          }
        }}
      >
        <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between gap-2 space-y-0">
          <DialogTitle className="text-base truncate flex-1 pr-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Make Fillable: {file?.filename}
          </DialogTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => detectFields("auto")}
              disabled={detecting || loading}
              className="gap-1"
              data-testid="detect-fillable-btn"
            >
              {detecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Auto-Detect
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => detectFields("visual")}
              disabled={detecting || loading}
              className="gap-1"
              title="Find drawn ☐ boxes and ______ blanks on flat (Word / printed) forms"
              data-testid="detect-visual-btn"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              Detect boxes &amp; blanks
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={applyI9Preset}
              disabled={loading || fields.length === 0}
              className="gap-1 border-teal-300 text-teal-700"
              title="Map I-9 Section 1 fields to the new hire's background-check answers and Section 2 fields to approved ID documents"
              data-testid="apply-i9-preset-btn"
            >
              <IdCard className="w-3.5 h-3.5" />
              I-9 preset
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={autoMapFields}
              disabled={loading || fields.length === 0}
              className="gap-1 border-emerald-300 text-emerald-700"
              title="Read every field label and set its auto-fill (client name, DOB, visit date, caregiver name…) in one pass"
              data-testid="auto-map-fields-btn"
            >
              <Wand2 className="w-3.5 h-3.5" />
              Auto-map fields
            </Button>
            {endpoints?.preview && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                disabled={loading || fields.length === 0}
                className="gap-1 border-sky-300 text-sky-700"
                title="See the form exactly as the nurse will, filled with a real client's information (nothing is saved)"
                data-testid="preview-as-nurse-btn"
              >
                <Eye className="w-3.5 h-3.5" />
                Preview as nurse
              </Button>
            )}
            <Button
              size="sm"
              onClick={saveConfig}
              disabled={saving || loading}
              className="gap-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-fillable-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Toolbar + field list (left) */}
          <aside className="w-72 border-r bg-slate-50 flex flex-col overflow-hidden">
            <div className="p-3 border-b space-y-2">
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={mode === "select" ? "default" : "outline"}
                  onClick={() => setMode("select")}
                  className="flex-1 gap-1"
                  data-testid="mode-select-btn"
                >
                  <MousePointer2 className="w-3.5 h-3.5" /> Select
                </Button>
                <Button
                  size="sm"
                  variant={mode === "draw" ? "default" : "outline"}
                  onClick={() => setMode("draw")}
                  className="flex-1 gap-1"
                  data-testid="mode-draw-btn"
                >
                  <MoveDiagonal className="w-3.5 h-3.5" /> Add fields
                </Button>
              </div>
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} data-testid="show-labels-toggle" />
                Show field names on the form (otherwise only on hover / selected)
              </label>
              {mode === "draw" && (
                <div data-testid="draw-mode-panel">
                  <label className="text-xs font-medium text-slate-600">Field type to place</label>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    {FIELD_TYPES.map((t) => (
                      <Button
                        key={t.value}
                        size="sm"
                        variant={drawType === t.value ? "default" : "outline"}
                        onClick={() => setDrawType(t.value)}
                        className="h-7 text-xs"
                        data-testid={`draw-type-${t.value}`}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    <b>Click</b> anywhere on the form to place a {drawType} field there, or <b>drag</b> for a custom size. Keep clicking to add more, then press <b>Select</b> when done.
                  </p>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">
                Fields ({fields.length})
              </p>
              {fields.length === 0 ? (
                <p className="text-xs text-slate-500 italic">
                  No fields yet. Click <b>Auto-Detect</b> for existing forms, <b>Detect boxes &amp; blanks</b> for printed / Word forms, or press <b>Add fields</b> and click on the form where the employee should write.
                </p>
              ) : (
                <ul className="space-y-1">
                  {fields.map((f) => (
                    <li
                      key={f.id}
                      onClick={() => setSelectedId(f.id)}
                      className={`p-2 rounded border cursor-pointer text-xs ${
                        selectedId === f.id ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate font-medium">{f.label}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {f.type}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Page {f.page + 1} · {f.assignee}
                        {f.required ? " · required" : ""}
                        {f.visit_slot !== null && f.visit_slot !== undefined ? <span className="ml-1 rounded bg-sky-100 text-sky-800 px-1" data-testid={`visit-slot-badge-${f.id}`}>{f.visit_slot === 0 ? "original" : `visit #${f.visit_slot}`}</span> : ""}
                      </div>
                      {(() => {
                        const af = fieldAutoFill(f, guesses[f.id]);
                        return af ? (
                          <div className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px]" data-testid={`autofill-badge-${f.id}`}>
                            <Wand2 className="w-2.5 h-2.5" /> Auto: {af.label}{af.detected ? " (detected)" : ""}
                          </div>
                        ) : null;
                      })()}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {selected && (
              <div className="border-t p-3 space-y-2 bg-white">
                <p className="text-xs font-semibold text-slate-700">Field details</p>
                <div>
                  <label className="text-[11px] text-slate-500">Label</label>
                  <Input
                    value={selected.label}
                    onChange={(e) => updateField(selected.id, { label: e.target.value })}
                    className="h-8"
                    data-testid="field-label-input"
                  />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {[["width", "W"], ["height", "H"], ["x", "X"], ["y", "Y"]].map(([k, lbl]) => (
                    <div key={k}>
                      <label className="text-[11px] text-slate-500">{lbl} (pt)</label>
                      <Input
                        type="number"
                        min={k === "width" || k === "height" ? 4 : 0}
                        step={1}
                        value={Math.round(selected[k])}
                        onChange={(e) => updateField(selected.id, { [k]: Math.max(k === "width" || k === "height" ? 4 : 0, Number(e.target.value) || 0) })}
                        className="h-8 px-1.5 text-xs"
                        data-testid={`field-${k}-input`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400">Tip: drag a box to move it, drag its bottom-right corner to resize.</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-500">Type</label>
                    <Select
                      value={selected.type}
                      onValueChange={(v) => updateField(selected.id, { type: v })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500">Filled by</label>
                    <Select
                      value={selected.assignee}
                      onValueChange={(v) => updateField(selected.id, { assignee: v })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="rn">RN co-signer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {selected.type !== "checkbox" && (
                  <div>
                    <label className="text-[11px] text-slate-500">Supervisory visit slot <span className="text-slate-400">(recurring forms, e.g. Plan of Care)</span></label>
                    <Select
                      value={selected.visit_slot === null || selected.visit_slot === undefined ? "none" : String(selected.visit_slot)}
                      onValueChange={(v) => updateField(selected.id, { visit_slot: v === "none" ? null : Number(v) })}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="visit-slot-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs">Every version (content carries over, editable)</SelectItem>
                        <SelectItem value="0" className="text-xs">Original fill only (visit #0)</SelectItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                          <SelectItem key={n} value={String(n)} className="text-xs" data-testid={`visit-slot-${n}`}>Supervisory update #{n} — signature / date row {n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selected.visit_slot !== null && selected.visit_slot !== undefined && (
                      <p className="text-[10px] text-sky-700 mt-1">Live only during {selected.visit_slot === 0 ? "the original fill" : `update #${selected.visit_slot}`}; locked with its earlier value on later updates, greyed out before then.</p>
                    )}
                  </div>
                )}
                {selected.assignee === "rn" && (
                  <p className="text-[11px] text-rose-800 rounded-md border border-rose-200 bg-rose-50 p-2" data-testid="rn-cosign-note">
                    After the employee submits, this form is routed to an RN who completes this field and signs on the spot. HR can pick the RN when assigning the form.
                  </p>
                )}
                {selected.assignee === "hr" && (
                  <div className="space-y-1.5 rounded-md border border-purple-200 bg-purple-50 p-2" data-testid="hr-prefill-panel">
                    <label className="text-[11px] font-medium text-purple-800">
                      Pre-filled value (shown to the employee, not editable)
                    </label>
                    {selected.type === "checkbox" ? (
                      <label className="flex items-center gap-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={String(selected.hr_value || "").toLowerCase() === "true"}
                          onChange={(e) => updateField(selected.id, { hr_value: e.target.checked ? "true" : "false" })}
                          data-testid="hr-prefill-checkbox"
                        />
                        Checked
                      </label>
                    ) : (
                      <>
                        <Input
                          className="h-8 text-xs bg-white"
                          value={selected.hr_value || ""}
                          onChange={(e) => updateField(selected.id, { hr_value: e.target.value })}
                          placeholder="e.g. {{agency_name}} or 12-3456789"
                          data-testid="hr-prefill-input"
                        />
                        <Select
                          value=""
                          onValueChange={(tok) =>
                            updateField(selected.id, { hr_value: `${selected.hr_value || ""}${tok}` })
                          }
                        >
                          <SelectTrigger className="h-7 text-[11px] bg-white" data-testid="hr-prefill-token-select">
                            <SelectValue placeholder="Insert agency / employee value…" />
                          </SelectTrigger>
                          <SelectContent>
                            {PREFILL_TOKENS.map((t) => (
                              <SelectItem key={t.token} value={t.token} className="text-xs">
                                {t.label} <span className="text-slate-400 ml-1">{t.token}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-purple-700">
                          Tokens fill in automatically per employee when the form is completed.
                        </p>
                      </>
                    )}
                  </div>
                )}
                {selected.type === "ink" && (
                  <p className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="ink-field-hint">
                    Drawing area — the nurse or employee handwrites or sketches inside this box (comments, body / wound diagrams). Make it as large as the printed box.
                  </p>
                )}
                {selected.assignee !== "hr" && selected.type !== "signature" && selected.type !== "ink" && (
                  <AutoFillPanel
                    field={selected}
                    guess={guesses[selected.id]}
                    onChange={(patch) => updateField(selected.id, patch)}
                  />
                )}
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!selected.required}
                    onChange={(e) => updateField(selected.id, { required: e.target.checked })}
                    data-testid="field-required-checkbox"
                  />
                  Required
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeField(selected.id)}
                  className="w-full gap-1 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-field-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete field
                </Button>
              </div>
            )}
          </aside>

          {/* PDF canvas */}
          <div ref={canvasRef} className="flex-1 overflow-auto bg-slate-200 p-4">
            {loading && (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
              </div>
            )}
            {pdfUrl && !loading && (
              <Document
                file={pdfUrl}
                loading={<Loader2 className="w-5 h-5 animate-spin text-slate-500" />}
                onLoadError={(e) => toast.error("PDF load error: " + e.message)}
              >
                {Array.from({ length: pageCount }, (_, i) => (
                  <div key={i} className="mb-4 flex justify-center">
                    <div
                      className="relative shadow-md bg-white inline-block"
                      onMouseDown={(e) => onPageMouseDown(i, e)}
                      onMouseMove={(e) => onPageMouseMove(i, e)}
                      onMouseUp={onPageMouseUp}
                      style={{ cursor: mode === "draw" ? "crosshair" : "default" }}
                    >
                      <Page
                        pageNumber={i + 1}
                        scale={renderScale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onRenderSuccess={({ width, height }) => {
                          setPageRenderInfo((prev) => {
                            const existing = prev[i];
                            if (existing && existing.width === width && existing.height === height) return prev;
                            return { ...prev, [i]: { width, height } };
                          });
                        }}
                      />

                      {/* Field overlays */}
                      {(fieldsByPage[i] || []).map((f) => {
                        const info = pageRenderInfo[i];
                        if (!info) return null;
                        const [wPts] = pageSizes[i] || [612, 792];
                        const scale = info.width / wPts;
                        const left = f.x * scale;
                        const bottom = f.y * scale;
                        const w = f.width * scale;
                        const h = f.height * scale;
                        const top = info.height - bottom - h;
                        const isSel = selectedId === f.id;
                        return (
                          <div
                            key={f.id}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              if (mode !== "select") return;
                              setSelectedId(f.id);
                              const container = e.currentTarget.parentElement;
                              const start = pxToPdf(i, e.clientX, e.clientY, container);
                              setManip({ kind: "move", id: f.id, page: i, startX: start.xPts, startY: start.yPts, orig: { x: f.x, y: f.y, width: f.width, height: f.height }, container });
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (mode === "select") setSelectedId(f.id);
                            }}
                            className={`group absolute border-2 rounded-sm ${mode === "select" ? "cursor-move" : "cursor-pointer"} ${TYPE_COLOR[f.type] || "border-slate-400 bg-slate-100/60"} ${isSel ? "ring-2 ring-blue-500 z-10" : ""}`}
                            style={{
                              left: `${left}px`,
                              top: `${top}px`,
                              width: `${w}px`,
                              height: `${h}px`,
                            }}
                            title={f.label}
                            data-testid={`editor-field-${f.id}`}
                          >
                            <span className={`absolute -top-4 left-0 text-[9px] text-slate-700 bg-white/90 px-1 rounded whitespace-nowrap max-w-[180px] truncate ${showLabels || isSel ? "" : "hidden group-hover:inline"}`}>
                              {f.label}
                            </span>
                            {(() => {
                              const af = fieldAutoFill(f, guesses[f.id]);
                              return af ? (
                                <span
                                  className={`absolute -top-4 right-0 text-[9px] px-1 rounded whitespace-nowrap max-w-[150px] truncate ${af.detected ? "bg-emerald-100 text-emerald-700" : "bg-emerald-600 text-white"} ${showLabels || isSel ? "" : "hidden group-hover:inline"}`}
                                  title={`Auto-fill: ${af.label}${af.detected ? " (detected from label)" : ""}`}
                                  data-testid={`editor-autofill-${f.id}`}
                                >
                                  Auto: {af.label}
                                </span>
                              ) : null;
                            })()}
                            {isSel && mode === "select" && (
                              <span
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  const container = e.currentTarget.parentElement.parentElement;
                                  const start = pxToPdf(i, e.clientX, e.clientY, container);
                                  setManip({ kind: "resize", id: f.id, page: i, startX: start.xPts, startY: start.yPts, orig: { x: f.x, y: f.y, width: f.width, height: f.height }, container });
                                }}
                                className="absolute -right-1.5 -bottom-1.5 w-3 h-3 rounded-sm bg-blue-600 border border-white cursor-nwse-resize"
                                title="Drag to resize"
                                data-testid={`editor-resize-${f.id}`}
                              />
                            )}
                          </div>
                        );
                      })}

                      {/* Live drawing preview */}
                      {drawing && drawing.page === i && pageRenderInfo[i] && (() => {
                        const info = pageRenderInfo[i];
                        const [wPts] = pageSizes[i] || [612, 792];
                        const scale = info.width / wPts;
                        const left = drawing.x * scale;
                        const bottom = drawing.y * scale;
                        const w = drawing.w * scale;
                        const h = drawing.h * scale;
                        const top = info.height - bottom - h;
                        return (
                          <div
                            className={`absolute border-2 border-dashed rounded-sm pointer-events-none ${TYPE_COLOR[drawType]}`}
                            style={{
                              left: `${left}px`,
                              top: `${top}px`,
                              width: `${w}px`,
                              height: `${h}px`,
                            }}
                          />
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </Document>
            )}
          </div>
        </div>
      </DialogContent>
      {previewOpen && (
        <NursePreviewDialog
          onClose={() => setPreviewOpen(false)}
          previewEndpoint={endpoints.preview}
          pdfEndpoint={endpoints.pdf}
          fields={fields}
          pageCount={pageCount}
          pageSizes={pageSizes}
          title={file?.filename}
        />
      )}
    </Dialog>
  );
}
